import { HasEmphasis } from '../../model';
import { Audited } from '@ianesthesie-kit/services';
import {
  BaseScorePayload,
  CardiacRiskFactorPayload,
  CpopPayload,
  Dn4Payload,
  HemstopPayload,
  LeePayload,
  MedicalScorePayload,
  Nrs2002Payload
} from './medicalScorePayload.model';

export enum MedicalScoreName {
  STOP_BANG = 'STOP_BANG',
  HEMSTOP = 'HEMSTOP',
  LEE = 'LEE',
  APFEL = 'APFEL',
  CHADSVASC = 'CHADSVASC',
  PREGNANCY_WEIGHT_GAIN = 'PREGNANCY_WEIGHT_GAIN',
  CFS = 'CFS',
  NRS_2002 = 'NRS_2002',
  ARISCAT = 'ARISCAT',
  AKI = 'AKI',
  GUPTA_POSTOP_RESPIRATORY_RISK = 'GUPTA_POSTOP_RESPIRATORY_RISK',
  GUPTA_POSTOP_PNEUMONIA = 'GUPTA_POSTOP_PNEUMONIA',
  CARDIAC_RISK_FACTOR = 'CARDIAC_RISK_FACTOR',
  CPOP = 'CPOP',
  DN4 = 'DN4',
  GAD_2 = 'GAD_2',
  RAI = 'RAI',
  ORT_R = 'ORT_R'
}

type BaseMedicalScore<
  N extends MedicalScoreName,
  P extends MedicalScorePayload
> = Audited &
  HasEmphasis & {
    name: N;
    score: number | null;
    formattedScore: string | null;
    finalized: boolean;
    payload: Partial<P>;
    applicable: boolean;
    locked: boolean;
    lockedAt?: string;
    patientContextAtLockingTime?: Record<string, any>;
  };

type RiskGroupClassification = {
  riskGroup?: 'LOW' | 'INTERMEDIATE' | 'HIGH';
};

export type ApfelScore = BaseMedicalScore<
  MedicalScoreName.APFEL,
  BaseScorePayload
>;
export type ChadsvascScore = BaseMedicalScore<
  MedicalScoreName.CHADSVASC,
  BaseScorePayload
>;
export type HemstopScore = BaseMedicalScore<
  MedicalScoreName.HEMSTOP,
  HemstopPayload
>;
export type LeeScore = BaseMedicalScore<MedicalScoreName.LEE, LeePayload>;
export type PregnancyWeightGain = BaseMedicalScore<
  MedicalScoreName.PREGNANCY_WEIGHT_GAIN,
  BaseScorePayload
>;
export type StopBangScore = BaseMedicalScore<
  MedicalScoreName.STOP_BANG,
  BaseScorePayload
>;
export type CfsScore = BaseMedicalScore<
  MedicalScoreName.CFS,
  BaseScorePayload
> & {
  thirtyMonthsSurvivability: number | null;
  seventyMonthsSurvivability: number | null;
};
export type Nrs2002Score = BaseMedicalScore<
  MedicalScoreName.NRS_2002,
  Nrs2002Payload
>;

export type AriscatScore = BaseMedicalScore<
  MedicalScoreName.ARISCAT,
  BaseScorePayload
> &
  RiskGroupClassification & {
    preopHb?: number;
    riskOfPostopPulmonaryComplications?: number;
  };
export type AkiScore = BaseMedicalScore<
  MedicalScoreName.AKI,
  BaseScorePayload
> & {
  majorComplicationRisk?: number;
  riskClass?: string;
};
export type GuptaPostopRespiratoryRiskScore = BaseMedicalScore<
  MedicalScoreName.GUPTA_POSTOP_RESPIRATORY_RISK,
  BaseScorePayload
>;
export type GuptaPostopPneumoniaScore = BaseMedicalScore<
  MedicalScoreName.GUPTA_POSTOP_PNEUMONIA,
  BaseScorePayload
>;

export type CardiacRiskFactorScore = BaseMedicalScore<
  MedicalScoreName.CARDIAC_RISK_FACTOR,
  CardiacRiskFactorPayload
>;

export type CpopScore = BaseMedicalScore<MedicalScoreName.CPOP, CpopPayload>;

export type Dn4Score = BaseMedicalScore<MedicalScoreName.DN4, Dn4Payload>;

export type Gad2Score = BaseMedicalScore<
  MedicalScoreName.GAD_2,
  BaseScorePayload
>;

export type OrtRScore = BaseMedicalScore<
  MedicalScoreName.ORT_R,
  BaseScorePayload
>;

export type RaiScore = BaseMedicalScore<
  MedicalScoreName.RAI,
  BaseScorePayload
> & {
  percentage?: number;
  oneYearMortality?: number;
  scoreCategory?: string;
};

export type MedicalScore =
  | ApfelScore
  | ChadsvascScore
  | HemstopScore
  | LeeScore
  | PregnancyWeightGain
  | StopBangScore
  | CfsScore
  | Nrs2002Score
  | AriscatScore
  | AkiScore
  | GuptaPostopRespiratoryRiskScore
  | GuptaPostopPneumoniaScore
  | CardiacRiskFactorScore
  | CpopScore
  | Dn4Score
  | Gad2Score
  | RaiScore
  | OrtRScore;
